/* eslint-disable jsx-a11y/media-has-caption */
import React from 'react'
import LazyLoad from 'react-lazyload'
import { getLink } from '@helpers/env'
import { useFitFinderToggle } from '@components/FitFinder/hooks/useFitFinderToggle'
import styles from './styles.module.scss'
import dynamic from 'next/dynamic'
const ResponsiveHOC = dynamic(() => import('@components/ui/ResponsiveHOC'))
import { Button, Device } from '.'

type BannerMediaProps = {
  desktop: Device
  tablet: Device
  mobile: Device
  desktopLarge: Device
}

interface HeroBannerMediaProps {
  button: Button
  desktop: Device
  tablet: Device
  mobile: Device
  desktopLarge: Device
  sendEvent: (event: Button['event']) => void
}

function getIsFileVideoContent(fileType?: string): boolean {
  return /mp4/.test(fileType || '')
}

const BannerImage = ({
  desktopLarge,
  desktop,
  tablet,
  mobile
}: BannerMediaProps) => (
  <picture className="bannerItem-image">
    {desktopLarge?.file && (
      <source
        media="(min-width: 1600px)"
        srcSet={`${desktopLarge.file.url}?fm=jpg&fl=progressive&w=3000`}
      />
    )}
    <source
      media={`(min-width: 1281px)${
        desktopLarge?.file ? ' and (max-width: 1599px)' : ''
      }`}
      srcSet={`${desktop.file.url}?fm=jpg&fl=progressive&w=2000`}
    />
    <source
      media="(min-width: 1000px) and (max-width: 1280px)"
      srcSet={`${desktop.file.url}?fm=jpg&fl=progressive&w=1400`}
    />
    <source
      media="(min-width: 600px) and (max-width: 1000px)"
      srcSet={`${tablet.file.url}?fm=jpg&fl=progressive&w=1000`}
    />
    <img
      alt={mobile.title}
      src={`${mobile.file.url}?fm=jpg&fl=progressive&w=600`}
    />
  </picture>
)

const BannerVideo = ({
  desktopLarge,
  desktop,
  tablet,
  mobile
}: BannerMediaProps) => (
  <>
    <ResponsiveHOC maxWidth={599}>
      <video {...videoAttrs}>
        <source src={mobile.file.url} type={mobile.file.contentType} />
      </video>
    </ResponsiveHOC>
    <ResponsiveHOC minWidth={600} maxWidth={999}>
      <video {...videoAttrs}>
        <source src={tablet.file.url} type={tablet.file.contentType} />
      </video>
    </ResponsiveHOC>
    <ResponsiveHOC minWidth={1000} maxWidth={1599}>
      <video {...videoAttrs}>
        <source src={desktop.file.url} type={desktop.file.contentType} />
      </video>
    </ResponsiveHOC>
    <ResponsiveHOC minWidth={1600}>
      <video {...videoAttrs}>
        <source
          src={(desktopLarge || desktop).file.url}
          type={(desktopLarge || desktop).file.contentType}
        />
      </video>
    </ResponsiveHOC>
  </>
)

const videoAttrs = {
  className: 'bannerItem-video',
  preload: 'metadata',
  autoPlay: true,
  loop: true,
  playsInline: true,
  crossOrigin: 'anonymous' as const,
  muted: true
}

export function HeroBannerMedia({
  button,
  desktop,
  desktopLarge,
  tablet,
  mobile,
  sendEvent
}: HeroBannerMediaProps) {
  const { openFitFinder } = useFitFinderToggle()
  const isVideo = getIsFileVideoContent(desktop.file.contentType)
  const Media = isVideo ? BannerVideo : BannerImage

  const handleClick = (ev: React.MouseEvent<HTMLAnchorElement>) => {
    if (ev.currentTarget.pathname.includes('/pages/fit-finder')) {
      ev.preventDefault()
      openFitFinder()
    }
    if (button?.event) {
      sendEvent(button.event)
    }
  }

  return (
    <div className={styles.bannerItem__media}>
      <LazyLoad once>
        <a
          href={getLink(button?.uri)}
          onClick={handleClick}
          className={styles.bannerItem__mediaLink}
        >
          <Media
            desktopLarge={desktopLarge}
            desktop={desktop}
            tablet={tablet}
            mobile={mobile}
          />
        </a>
      </LazyLoad>
    </div>
  )
}
